const SYSTEM_TAG_CATEGORIES = [
  { name: "Hook", keys: ["hook_tactic"] },
  {
    name: "Messaging & script",
    keys: ["emotional_territory", "messaging_angle", "script"],
  },
  { name: "Visuals", keys: ["format", "setting", "sound_off_rating"] },
  { name: "Audience", keys: ["target_persona"] },
  {
    name: "Offer & CTA",
    keys: ["cta_style", "offer_type", "product_featured"],
  },
  {
    name: "Talent",
    keys: ["performer_type", "performer_gender", "performer_generation"],
  },
  { name: "Context", keys: ["seasonality"] },
] as const;

/** Tags a workspace created, which no built-in category covers. */
export const CUSTOM_TAG_CATEGORY = "Custom";

export type CreativeTagCategory =
  | (typeof SYSTEM_TAG_CATEGORIES)[number]["name"]
  | typeof CUSTOM_TAG_CATEGORY;

const CATEGORY_BY_KEY: ReadonlyMap<string, CreativeTagCategory> = new Map(
  SYSTEM_TAG_CATEGORIES.flatMap(({ name, keys }) =>
    keys.map((key) => [key, name] as const),
  ),
);

const CATEGORY_ORDER: readonly CreativeTagCategory[] = [
  ...SYSTEM_TAG_CATEGORIES.map(({ name }) => name),
  CUSTOM_TAG_CATEGORY,
];

export function creativeTagCategory(dimensionKey: string): CreativeTagCategory {
  return CATEGORY_BY_KEY.get(dimensionKey) ?? CUSTOM_TAG_CATEGORY;
}

/**
 * Tags under their categories, in the categories' fixed order with Custom
 * last. Empty categories are left out, and tags sort by name within one.
 */
export function groupTagsByCategory<
  Tag extends { dimensionKey: string; dimensionName: string },
>(tags: readonly Tag[]): { category: CreativeTagCategory; tags: Tag[] }[] {
  const byCategory = new Map<CreativeTagCategory, Tag[]>();
  for (const tag of tags) {
    const category = creativeTagCategory(tag.dimensionKey);
    byCategory.set(category, [...(byCategory.get(category) ?? []), tag]);
  }
  return CATEGORY_ORDER.flatMap((category) => {
    const inCategory = byCategory.get(category);
    return inCategory
      ? [
          {
            category,
            tags: [...inCategory].sort((a, b) =>
              a.dimensionName.localeCompare(b.dimensionName),
            ),
          },
        ]
      : [];
  });
}
